/**
 * The last local recovery step: a card whose paragraphs the user deleted is placed
 * where they stood, from the neighbours the card remembers, so it is written back
 * there before any server or model is asked.
 *
 * The earlier steps look for the card's own paragraph. This one runs when none of
 * them found it, and decides from the card's own anchors that it is gone: no body
 * paragraph reads as the card's text, and the paragraphs the card remembers above
 * and below now stand next to each other. It then places the card after the
 * neighbour above (`after_paragraph`), or before the neighbour below
 * (`before_paragraph`) when the one above is gone too. Apply writes a card so
 * placed as new paragraphs beside that neighbour (`RedlineSource.placedFor`).
 *
 * Every text comparison goes through the engine's one reading
 * (`WordTokenizer.readsTheSame`).
 */
import type { NeighbourParagraph, ParagraphAnchor } from "@common/utils/redline/paragraph-locator";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { RedlinePlacement } from "@common/utils/redline/wire/redline-placement";

import type { RecoveryContext, RecoveryStep } from "./recovery-chain";
import { RecoveryResult } from "./recovery-result";
import type { BodyAnchorParagraph } from "../word-document";

/** The card written as new paragraphs right after the neighbour it is laid on. */
const AFTER_PARAGRAPH: RedlinePlacement = { kind: "after_paragraph" };

/** The card written as new paragraphs right before the neighbour it is laid on. */
const BEFORE_PARAGRAPH: RedlinePlacement = { kind: "before_paragraph" };

export class DeletedParagraphStep implements RecoveryStep {
    readonly tier = "deleted-paragraph";
    readonly triggers = "any";

    /**
     * The card placed beside the paragraph that stood next to its deleted ones.
     *
     * CASES
     *   - the card writes no words back (it only deletes, or it
     *     is a comment's paragraph)                              -> not found: nothing of it goes back
     *                                                               (`redlineWritesNothingWhereItsParagraphsAreGone`)
     *   - a body paragraph reads as one of the card's texts       -> not found: the paragraph is there, and a
     *                                                               later step may place the card on it
     *   - the neighbour above stands, and the neighbour below
     *     stands right after it, or is gone, or was never stored -> after the neighbour above
     *   - the neighbour above stands, and something else stands
     *     between it and the neighbour below                      -> not found: that may be the card's
     *                                                               paragraph, retyped
     *   - the neighbour above is gone, the one below stands       -> before the neighbour below
     *   - neither stands                                           -> not found
     *
     * EXAMPLE
     *   card on 8.1 "Severity 1 … four (4) hours." (above 7.9, below 8.2), 8.1 deleted by the user
     *   body [… 7.9, 8.2 …] -> 8.2 stands right after 7.9 -> after_paragraph on 7.9
     */
    async recover({ memory, cardWritesWhereItsParagraphsAreGone, body: recoveryBody }: RecoveryContext): Promise<RecoveryResult> {
        /**
         * WHEN the card writes no words back where its paragraphs are gone (it only deletes them,
         *      or it is a comment's or a reply's paragraph, whose text it leaves as it is)
         *   CONSEQUENCE
         *     it is placed nowhere: nothing of it goes back, and a comment on a deleted paragraph
         *     is labelled as such, never moved onto a neighbour
         */
        if (!cardWritesWhereItsParagraphsAreGone) return RecoveryResult.notFound(null);
        const members = memory.toSpanAnchor().members;
        const body = recoveryBody.anchorParagraphs;
        const cardTextStillReads = members.some((member) => WordTokenizer.words(member.text).length > 0 && body.some((paragraph) => WordTokenizer.readsTheSame(paragraph.text, member.text)));
        /** WHEN a body paragraph still reads as the card's text CONSEQUENCE the card's paragraph is not gone. */
        if (cardTextStillReads) return RecoveryResult.notFound(null);
        const firstMember: ParagraphAnchor | undefined = members[0];
        const lastMember: ParagraphAnchor | undefined = members[members.length - 1];
        const neighbourAbove = firstMember?.neighbourAbove ? DeletedParagraphStep.bodyParagraphOf(firstMember.neighbourAbove, body) : null;
        const neighbourBelow = lastMember?.neighbourBelow ? DeletedParagraphStep.bodyParagraphOf(lastMember.neighbourBelow, body) : null;
        /**
         * CASES, for the neighbour above
         *   - it stands, and nothing but the neighbour below (or nothing known) follows it
         *     -> the card goes after it
         *   - it stands, and another paragraph stands between it and the neighbour below
         *     -> not found
         *
         * EXAMPLE
         *   above at walk index 41, below at 42 -> 42 = 41 + 1: nothing between them
         *   above at 41, below at 43            -> paragraph 42 stands between them
         */
        if (neighbourAbove !== null) {
            const nothingStandsBetween = neighbourBelow === null || neighbourBelow.walkIndex === neighbourAbove.walkIndex + 1;
            if (!nothingStandsBetween) return RecoveryResult.notFound("a paragraph stands between the card's remembered neighbours, so its own may still be there");
            return DeletedParagraphStep.placedBeside(neighbourAbove, AFTER_PARAGRAPH, "the card's paragraph is gone; it goes back after the paragraph that stood above it");
        }
        /** WHEN only the neighbour below stands CONSEQUENCE the card goes back before it. */
        if (neighbourBelow !== null) return DeletedParagraphStep.placedBeside(neighbourBelow, BEFORE_PARAGRAPH, "the card's paragraph and the one above it are gone; it goes back before the paragraph that stood below it");
        return RecoveryResult.notFound(null);
    }

    /**
     * The card placed beside a body paragraph.
     *
     * CASES
     *   - Word lists a paragraph for it -> recovered beside it
     *   - it lists none                 -> not found: there is nothing to lay the card's bookmarks on
     */
    private static placedBeside(neighbour: BodyAnchorParagraph, placement: RedlinePlacement, explanation: string): RecoveryResult {
        if (neighbour.paragraph === null) return RecoveryResult.notFound("the paragraph beside the card's is one Word lists no paragraph for");
        return RecoveryResult.recovered({ paragraphs: [neighbour.paragraph], placement, explanation, checks: null });
    }

    /**
     * The body paragraph a remembered neighbour is now.
     *
     * CASES
     *   - its paraId is on exactly one body paragraph          -> that paragraph
     *   - exactly one body paragraph reads as its text         -> that paragraph
     *   - it holds no word, or several paragraphs fit, or none -> null: it cannot be told apart
     *
     * NULL
     *   The neighbour cannot be found as one paragraph.
     */
    private static bodyParagraphOf(neighbour: NeighbourParagraph, body: BodyAnchorParagraph[]): BodyAnchorParagraph | null {
        const byId = neighbour.paraId === null ? [] : body.filter((paragraph) => paragraph.paraId === neighbour.paraId);
        if (byId.length === 1) return byId[0];
        /** WHEN the neighbour holds no word CONSEQUENCE every blank reads as it, so it cannot be told apart. */
        if (WordTokenizer.words(neighbour.text).length === 0) return null;
        const byText = body.filter((paragraph) => WordTokenizer.readsTheSame(paragraph.text, neighbour.text));
        return byText.length === 1 ? byText[0] : null;
    }
}
